/**
 * ============================================================================
 * Runtime Configuration Helper
 * ============================================================================
 * 
 * This module provides access to runtime configuration loaded from config.js
 * instead of build-time environment variables.
 * 
 * This allows changing configuration without rebuilding the application.
 * 
 * Usage:
 *   import { getRuntimeConfig } from '@/lib/runtimeConfig';
 *   const config = getRuntimeConfig();
 *   const supabaseUrl = config.VITE_SUPABASE_URL;
 * 
 * ============================================================================
 */

interface RuntimeConfig {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
  VITE_APP_TITLE?: string;
  VITE_APP_LOGO?: string;
  VITE_OAUTH_PORTAL_URL?: string;
  VITE_FRONTEND_FORGE_API_KEY?: string;
  VITE_FRONTEND_FORGE_API_URL?: string;
  __CONFIG_MISSING__?: boolean;
  [key: string]: string | boolean | undefined;
}

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Get runtime configuration
 * Falls back to import.meta.env if runtime config is not available (dev mode)
 */
export function getRuntimeConfig(): RuntimeConfig {
  // In production, use runtime config from config.js
  if (window.__APP_CONFIG__) {
    return window.__APP_CONFIG__;
  }
  
  // In development, fall back to Vite's import.meta.env
  if (import.meta.env.DEV) {
    return {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
      VITE_APP_TITLE: import.meta.env.VITE_APP_TITLE,
      VITE_APP_LOGO: import.meta.env.VITE_APP_LOGO,
      VITE_OAUTH_PORTAL_URL: import.meta.env.VITE_OAUTH_PORTAL_URL,
      VITE_FRONTEND_FORGE_API_KEY: import.meta.env.VITE_FRONTEND_FORGE_API_KEY,
      VITE_FRONTEND_FORGE_API_URL: import.meta.env.VITE_FRONTEND_FORGE_API_URL,
    };
  }
  
  // No config available
  return {
    __CONFIG_MISSING__: true,
  };
}

/**
 * Check if configuration is valid
 */
export function isConfigValid(): boolean {
  const config = getRuntimeConfig();
  
  if (config.__CONFIG_MISSING__) {
    return false;
  }
  
  // Check required variables
  const required = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'];
  return required.every(key => config[key] && config[key] !== '');
}

/**
 * Get missing configuration keys
 */
export function getMissingConfigKeys(): string[] {
  const config = getRuntimeConfig();
  const required = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'];
  
  return required.filter(key => !config[key] || config[key] === '');
}
